<template lang="pug">
    .CounterControlContainer(:class="{ 'SideControls' : sideControls }")
        .ControlsArea
            .LeftButtonArea.ButtonArea(:style="sideControlsStyles.leftButtonArea")
                v-btn.CustomButton#decrement-button(
                    icon
                    small
                    color="red darken-2"
                    @click="substractOneToCounter"
                    dark
                    )
                    span.ButtonIcon.display-2 -

            .TextInputArea(:style="sideControlsStyles.inputArea")
                input.font-weight-bold(
                    ref="counterInput"
                    v-model="displayValue"
                    :maxlength="inputLength"
                    @blur="inputBlurHandler"
                    type="number"
                    )

            .RightButtonArea.ButtonArea
                v-btn.CustomButton#increment-button(
                    icon
                    small
                    color="green darken-2"
                    @click="addOneToCounter"
                    dark
                ) 
                    span.ButtonIcon.headline.font-weight-bold +
                    
        .InfoArea(v-if="showInfoArea")
            span.InfoLabel.font-weight-light {{infoLabel}}
            span.InfoValue {{infoValue}}
</template>

<script>
/**
 * This componed is a simple, styled counter intended to be used mainly
 * on carts or other lists of products for the system. 
 */
export default {
    name: 'counter-control',
    data: function() {
        return {
            /**
             * @description
             * Due to problems on reactivity with nested object properties, 
             * we clone the value property here, for displaying and event dispatching
             * purposes.
             * @type {number}
             */
            displayValue: 0,
        }
    },
    props: {
        /**
         * @description
         * Incoming number to count or modify.
         * @type {number | string}
         */
        value: {
            default: 0,
        },
        /**
         * @description
         * Max digit/char length allowed for this component's input
         * (includes floating point).
         * @type {integer}
         */
        inputLength: {
            type: Number,
            default: 5,
        },
        /**
         * @description
         * CSS width for the component's input. Used with inputWidthUnit
         * to assign ammount and unit.
         * @type {number}
         */
        inputWidth: {
            type: Number,
            default: 60,
        },
        /**
         * @description
         * CSS unit for the component's input width. Used with inputWidth
         * to assign ammount and unit. Ex: vh, vw, px, em, %.
         * 
         */
        inputWidthUnit: {
            type: String,
            default: 'px',
        },
        /**
         * @description
         * Optional label to go under the component's input. Used alogn with infoValue
         * allows to show a label - vlaue pair.
         * @type {string}
         */
        infoLabel: {
            type: String,
            default: '',
        },
        /**
         * @description
         * Optional value to show along InfoLabel
         * @type {any}
         */
        infoValue: {
            default: '',
        },
        /**
         * @description
         * Minimum allowed value for the counted/controlled number
         * @type {number}
         */
        minValue: {
            type: Number,
            default: 0,
        },
        /**
         * @description
         * Maximum allowed value for the counted/controlled number
         * @type {number}
         */
        maxValue: {
            type: Number,
            default: 9999,
        },
        /**
         * @description
         * Toggles the counter mode to handle float numbers
         * @type {bool}
         */
        allowFloat: {
            type: Boolean,
            default: false,
        },
        /**
         * @description
         * Toggles the display mode for the counter. False for buttons
         * in each side of the input, true for both buttons on the right.
         * @type {bool}
         */
        sideControls: {
            type: Boolean,
            default: true,
        },
    },
    computed: {
        /**
         * @description
         * Toggles the info area under the input. If there's data to be 
         * showed on that area, it will automatically display it.
         * @returns {bool}
         */
        showInfoArea: function() {
            return this.infoValue || this.infoLabel ? true : false
        },
        /**
         * @description
         * Concatenates inputWidth and inputWidthUnit to get the CSS
         * for input width.
         * @returns {string}
         */
        getFullInputWidth: function() {
            return this.inputWidth + this.inputWidthUnit
        },
        /**
         * @description
         * Toggles between side controls mode and normal mode. 
         * Side controls mode shows both counter buttons on right.
         * Normal mode shows decrement to left and increment to right.
         * @returns {object}
         */
        sideControlsStyles: function() {
            let leftButtonArea = this.sideControls
                ?   {
                        left: 'calc(' + this.getFullInputWidth + ' + 30px)',
                    }
                : {}
            let inputArea =
                this.sideControls && !this.showInfoArea
                    ?   {
                            width: this.getFullInputWidth,
                            'margin-top': '9px',
                        }
                    : { width: this.getFullInputWidth }
            return {
                leftButtonArea,
                inputArea,
            }
        },
    },
    methods: {
        /**
         * @description
         * Emits input event for parent of this component to recover the 
         * updated counted value.
         * @param {number} value - Counted Value.
         */
        emitValueChangeEvent: function(value) {
            this.$emit('input', value)
        },
        /**
         * @description
         * Increments counter by 1.
         */
        addOneToCounter: function() {
            this.displayValue++
        },
        /**
         * @description
         * Decrements counter by 1.
         */
        substractOneToCounter: function() {
            this.displayValue--
        },
        /**
         * @description
         * Validation for value not being less than the minimum allowed value.
         * @param {number} value - Number to check.
         * @returns {bool}
         */
        validateMin: function(value) {
            if (value < this.minValue) return false
            return true
        },
        /**
         * @description
         * Validation for value not being more than the minimum allowed value.
         * @param {number} value - Number to check.
         * @returns {bool}
         */
        validateMax: function(value) {
            if (value > this.maxValue) return false
            return true
        },
        /**
         * @description
         * Check if float numbers are allowed and validates according to that.
         * @param {number} value - Number to check.
         * @returns {bool}
         */
        validateFloat: function(value) {
            if (this.allowFloat) return true
            if (!this.allowFloat && value % 1 != 0) {
                return false
            }
            return true
        },
        /**
         * @description
         * Sets input to the minimum value if input is empty.
         * Used when input is empty and loses focus
         */
        inputBlurHandler: function() {
            if (this.displayValue === '') this.displayValue = this.minValue
        },
    },
    /**
     * For all watchers, newVal refers to the new Value, and oldVal to the 
     * updated value.
     */
    watch: {
        /**
         * @description
         * Watches incoming value prop, to reassign displayed value.
         * @param {any} newVal
         * @param {any} oldVal
         */
        value: function(newVal, oldVal) {
            this.displayValue = newVal
        },
        /**
         * @description
         * Watches displayed value, for validation, or for emiting the updated
         * value to parent component when all validations are correct.
         * @param {any} newVal
         * @param {any} oldVal
         */
        displayValue: function(newVal, oldVal) {
            if (newVal === '') return
            if (!this.validateMin(newVal)) {
                this.displayValue = this.minValue
                this.emitValueChangeEvent(this.minValue)
                return
            }
            if (!this.validateMax(newVal)) {
                this.displayValue = this.maxValue
                this.emitValueChangeEvent(this.maxValue)
                return
            }
            if (!this.validateFloat(newVal)) {
                this.displayValue = Math.floor(newVal)
                this.emitValueChangeEvent(this.displayValue)
                return
            }
            this.emitValueChangeEvent(newVal)
        },
    },
    /**
     * @description
     * when mounted assigns for the first time the value prop to the 
     * display data prop.
     */
    mounted: function() {
        if (this.value) this.displayValue = this.value
    },
}
</script>

<style lang="scss" scoped>
.CounterControlContainer {
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    min-width: 100px;
    width: 100%;
    justify-content: center;

    .ControlsArea {
        width: 100%;
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        justify-content: center;
        min-width: 100px;
        height: 25px;
        margin-top: -2px;
    }
    .InfoArea {
        width: 100%;
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        justify-content: center;
        min-width: 100px;
        padding-left: 10px;
    }
    .ButtonArea {
        width: 25px;
        .CustomButton {
            width: 20px;
            height: 20px;
        }
    }
    .LeftButtonArea {
        margin-right: 10px;
        .ButtonIcon {
            margin-top: -15px;
            margin-right: -1px;
        }
    }
    .RightButtonArea {
        .ButtonIcon {
            margin-top: -6px;
            margin-right: -1px;
        }
    }

    .TextInputArea input {
        margin-top: 5px;
        border: 1px solid gray;
        border-radius: 5px;
        text-align: center;
        width: 100%;
        height: 22px;
    }

    &.SideControls {
        margin-left: -10px;
        .ButtonArea {
            width: 20px;
            .CustomButton {
                width: 15px;
                height: 15px;
            }
        }
        .LeftButtonArea {
            position: relative;
            margin-right: 10px;
            margin-top: 23px;
            .ButtonIcon {
                margin-top: -18px;
            }
        }
        .RightButtonArea {
            margin-top: 1px;
            .ButtonIcon {
                margin-top: -9px;
                margin-right: -1px;
            }
        }
    }
    /* Disable arrows from number input */
    input[type="number"]::-webkit-outer-spin-button, input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
    }
    
    input[type="number"] {
        -moz-appearance: textfield;
    }
}
</style>
